/**
 * Axis-aligned bounding-box collision detection.
 * Ported from `testCollisionGenerique` and `testCollisionMur`
 * (`Jeu.c:88` / `Jeu.c:99`).
 */

import { bikeBox, type Bike } from "./bike";
import type { Segment } from "./trail";
import type { AABB } from "./types";
import { Direction } from "./types";

/**
 * True if the two boxes actually overlap (strict inequalities: two boxes that
 * merely touch along an edge do not collide, exactly as in the C version).
 */
export function overlaps(a: AABB, b: AABB): boolean {
  return a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];
}

/** Box of a rectangle given by top-left corner + dimensions. */
export function boxOf(x: number, y: number, w: number, h: number): AABB {
  return [x, y, x + w, y + h];
}

function segBox(s: Segment): AABB {
  return [s.x, s.y, s.x + s.w, s.y + s.h];
}

/** Is a trail lethal to this colour? (a fresh trail of one's own colour is safe) */
function isDeadly(seg: Segment, color: Bike["color"]): boolean {
  return seg.life < seg.maxLife || seg.color !== color;
}

export const CollisionResult = {
  None: 0,
  Hit: 1,
  /** High-speed graze: one tick of reprieve (DOUBT state). */
  Graze: 2,
} as const;
export type CollisionResult = (typeof CollisionResult)[keyof typeof CollisionResult];

/** Minimum speed for the "graze" case to apply (`Jeu.c:133`). */
export const GRAZE_MIN_SPEED = 10;

export interface GridBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Tests the bike against the arena edges, then against every trail segment.
 * Returns `Hit` (death) if the bike leaves the arena or strikes a lethal trail,
 * `Graze` if its last corner segment grazes a trail at high speed, `None`
 * otherwise. `jumping` skips the trail test entirely (JUMP bonus).
 */
export function bikeCollision(
  bike: Bike,
  grid: GridBounds,
  segments: readonly Segment[],
  jumping: boolean,
): CollisionResult {
  const box = bikeBox(bike);
  if (
    box[0] < grid.x ||
    box[2] > grid.x + grid.width ||
    box[1] < grid.y ||
    box[3] > grid.y + grid.height
  ) {
    return CollisionResult.Hit;
  }
  if (jumping) return CollisionResult.None;

  const last = bike.lastSegment;
  const lastBox = last ? segBox(last) : null;
  const fast = bike.speed >= GRAZE_MIN_SPEED;
  const vertical = bike.dir === Direction.Up || bike.dir === Direction.Down;

  for (const seg of segments) {
    const sBox = segBox(seg);

    if (overlaps(box, sBox) && isDeadly(seg, bike.color)) {
      return CollisionResult.Hit;
    }

    if (
      fast &&
      last &&
      lastBox &&
      overlaps(lastBox, sBox) &&
      (seg.life < seg.maxLife || seg.color !== last.color) &&
      ((vertical && lastBox[0] !== sBox[0]) || (!vertical && lastBox[1] !== sBox[1]))
    ) {
      return CollisionResult.Graze;
    }
  }

  return CollisionResult.None;
}

/** Bike-to-bike collision (`testCollisionMoto`, `Jeu.c:145`). */
export function bikesOverlap(a: Bike, b: Bike): boolean {
  return overlaps(bikeBox(a), bikeBox(b));
}
